import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { KpiCards } from './revenue/KpiCards';
import { RevenueChart } from './revenue/RevenueChart';
import { MrrTrendChart } from './revenue/MrrTrendChart';
import { TierBreakdown } from './revenue/TierBreakdown';
import { TopCustomersTable } from './revenue/TopCustomersTable';

interface RevenueStats {
  excludeTestStripe: boolean;
  generatedAt: string;
  mrr: { current: number; previous: number; deltaPct: number };
  arr: number;
  monthRevenue: { current: number; previous: number; deltaPct: number };
  activePayingCustomers: { current: number; previous: number; deltaPct: number };
  tierBreakdown: Array<{ tier: string; count: number; mrr: number; arpu: number }>;
  monthlySeries: Array<{
    month: string;
    subscriptions: number;
    topups: number;
    total: number;
    mrrSnapshot: number;
    topupCount: number;
  }>;
  topCustomers: Array<{
    user_id: string;
    user_email: string;
    subs_total: number;
    topups_total: number;
    total: number;
    subs_count: number;
    topups_count: number;
    last_payment_at: string | null;
  }>;
}

export function RevenueManager() {
  const [stats, setStats] = useState<RevenueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [excludeTestStripe, setExcludeTestStripe] = useState(true);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Use direct fetch so we can pass a query string (supabase.functions.invoke doesn't support this cleanly)
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-revenue-stats?excludeTestStripe=${excludeTestStripe}`;
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json = (await res.json()) as RevenueStats;
      setStats(json);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast({ title: 'Failed to load revenue', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [excludeTestStripe, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !stats) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Crunching revenue numbers…</p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Switch
            id="exclude-test-stripe"
            checked={excludeTestStripe}
            onCheckedChange={setExcludeTestStripe}
          />
          <Label htmlFor="exclude-test-stripe" className="text-sm cursor-pointer">
            Exclude test-stripe users
          </Label>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <KpiCards
        mrr={stats.mrr}
        arr={stats.arr}
        monthRevenue={stats.monthRevenue}
        activePayingCustomers={stats.activePayingCustomers}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueChart data={stats.monthlySeries} />
        <MrrTrendChart data={stats.monthlySeries} />
      </div>

      <TierBreakdown data={stats.tierBreakdown} />

      <TopCustomersTable data={stats.topCustomers} />

      <p className="text-xs text-muted-foreground text-right">
        Generated at {new Date(stats.generatedAt).toLocaleString('de-DE')}
      </p>
    </div>
  );
}
